import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Stack from "@mui/material/Stack";
import { Link } from "react-router";
import Logo from "../../../assets/images/zimba-z.png";
import { Avatar, useMediaQuery, Theme } from "@mui/material";
import NotificationsPopover from "../popovers/NotificationsPopover";
import ProfilePopover from "../popovers/ProfilePopover";

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: "solid",
  borderColor: (theme.vars ?? theme).palette.divider,
  boxShadow: "none",
  zIndex: theme.zIndex.drawer - 1,
}));

const LogoContainer = styled("div")({
  position: "relative",
  width: 180,
  height: 10,
  display: "flex",
  alignItems: "center",
  "& img": {
    maxHeight: 40,
  },
});

const ToggleIcon = styled(Box)(({ theme }) => ({
  width: 25,
  height: 3,
  margin: "5px",
  borderRadius: "10px",
  transition: "width 0.3s",
  backgroundColor: theme.palette.primary.main,
}));

export interface DashboardHeaderProps {
  logo?: React.ReactNode;
  title?: string;
  menuOpen: boolean;
  onToggleMenu: (open: boolean) => void;
  setShowMobileSideBar: () => void;
}

export default function DashboardHeader({
  logo,
  title,
  menuOpen,
  onToggleMenu,
  setShowMobileSideBar,
}: DashboardHeaderProps) {
  const theme = useTheme();
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const downSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const downLg = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const handleMenuOpen = React.useCallback(() => {
    onToggleMenu(!menuOpen);
  }, [menuOpen, onToggleMenu]);

  const getMenuIcon = React.useCallback(
    (isExpanded: boolean) => {
      const expandMenuActionText = "Expand";
      const collapseMenuActionText = "Collapse";

      return (
        <Tooltip
          title={`${
            isExpanded ? collapseMenuActionText : expandMenuActionText
          } menu`}
          enterDelay={1000}
        >
          <div>
            <IconButton
              size="small"
              aria-label={`${
                isExpanded ? collapseMenuActionText : expandMenuActionText
              } navigation menu`}
              onClick={handleMenuOpen}
            >
              <MenuIcon />
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    [handleMenuOpen],
  );

  return (
    <AppBar color="inherit" position="absolute" sx={{ displayPrint: "none" }}>
      <Toolbar sx={{ backgroundColor: "inherit", mx: { xs: -0.75, sm: -1 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <Stack direction="row" alignItems="center">
            <Link to="/" style={{ textDecoration: "none" }}>
              <Stack direction="row" alignItems="center">
                {!downMd && (
                  <LogoContainer>
                    <Avatar
                      alt="Zimba Logo"
                      src={Logo}
                      sx={{ width: 140, height: 50 }}
                    />
                  </LogoContainer>
                )}
                {title ? (
                  <Typography
                    variant="h6"
                    sx={{
                      color: (theme.vars ?? theme).palette.primary.main,
                      fontWeight: "700",
                      ml: 1,
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    {title}
                  </Typography>
                ) : null}
              </Stack>
            </Link>
            <Box
              sx={{
                cursor: downSm ? "pointer" : "none",
                display: downSm || downMd ? "block" : "none",
              }}
              onClick={setShowMobileSideBar}
            >
              <ToggleIcon />
              <ToggleIcon />
              <ToggleIcon />
            </Box>
            <Box sx={{ mr: 1, display: downSm || downMd ? "none" : "block" }}>
              {getMenuIcon(menuOpen)}
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ marginLeft: "auto" }}
          >
            <Stack direction="row" alignItems="center">
              <Box sx={{ borderRight: "1px solid #D9D9D9", paddingX: 2 }}>
                <NotificationsPopover />
              </Box>
              <Box sx={{ paddingX: 2, width: 180 }}>
                <ProfilePopover />
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
